import React, { useState, useEffect } from 'react';
import { Container, Table, Button, Alert, Spinner, Pagination, ButtonGroup } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import api from '../api';
import RefreshConnectionButton from '../components/RefreshConnectionButton';

const AuthenticatedImage = ({ src, alt, sku, platform }) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [error, setError] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchImage = async () => {
      if (src && src.startsWith('http')) {
        const img = new Image();
        img.src = src;
        img.onload = () => setImageSrc(src);
        img.onerror = () => {
          console.warn(`Direct load failed for ${platform} image ${sku}, falling back to proxy`);
          fetchViaProxy();
        };
        return;
      }
      if (platform === 'Walmart' && src) fetchViaProxy();
    };

    const fetchViaProxy = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          console.warn('No token found, redirecting to login');
          navigate('/login');
          return;
        }
        console.log(`Fetching ${platform} image for SKU ${sku}:`, src);
        const response = await api.get(`/proxy-image?url=${encodeURIComponent(src)}`, {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob',
        });
        const url = URL.createObjectURL(response.data);
        setImageSrc(url);
      } catch (err) {
        console.error(`Failed to load ${platform} image for SKU ${sku}:`, src, err);
        if (err.response?.status === 401) {
          console.warn('401 Unauthorized: Redirecting to login');
          localStorage.removeItem('token');
          navigate('/login');
        } else {
          setError(true);
        }
      }
    };

    if (src) {
      fetchImage();
    } else {
      setError(true);
    }

    return () => {
      if (imageSrc && imageSrc !== src) URL.revokeObjectURL(imageSrc);
    };
  }, [src, sku, platform, navigate]);

  if (error || !src) return (
    <span>
      Image Failed to Load
      <img
        src="/default-image-url.jpg"
        alt="Default"
        style={{ maxWidth: '50px', maxHeight: '50px', objectFit: 'cover' }}
      />
    </span>
  );
  if (!imageSrc) return <Spinner animation="border" size="sm" />;
  return <img src={imageSrc} alt={alt} style={{ maxWidth: '50px', maxHeight: '50px', objectFit: 'cover' }} />;
};

function WalmartInventory() {
  const [products, setProducts] = useState(() => {
    const savedProducts = localStorage.getItem('walmartProducts');
    return savedProducts ? JSON.parse(savedProducts) : [];
  });
  const [loading, setLoading] = useState(true);
  const [connectionLoading, setConnectionLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  const checkAuthAndRedirect = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('No token found, redirecting to login');
      navigate('/login');
      return false;
    }
    return true;
  };

  const checkConnection = async (retryCount = 0, maxRetries = 3) => {
    if (!checkAuthAndRedirect()) return;
    try {
      setConnectionLoading(true);
      console.log('Checking Walmart connection status...');
      const token = localStorage.getItem('token');
      const response = await api.get('/walmart/status', {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log('Walmart connection status response:', response.data);
      setIsConnected(response.data.connected);
      if (response.data.connected) {
        await fetchProducts(page);
      } else {
        setError('Walmart is not connected. Please contact support.');
      }
    } catch (err) {
      console.error('Error checking Walmart status:', err);
      if (err.response?.status === 401) {
        console.warn('401 Unauthorized: Redirecting to login');
        localStorage.removeItem('token');
        navigate('/login');
        return;
      }
      if (retryCount < maxRetries) {
        console.log(`Retrying Walmart connection check (${retryCount + 1}/${maxRetries})...`);
        await new Promise(resolve => setTimeout(resolve, 1000));
        return checkConnection(retryCount + 1, maxRetries);
      }
      setIsConnected(false);
      setError('Failed to check Walmart connection status after retries.');
    } finally {
      setConnectionLoading(false);
    }
  };

  const fetchProducts = async (pageNum = 1) => {
    if (!checkAuthAndRedirect()) return;
    try {
      setLoading(true);
      console.log('Fetching Walmart products for page:', pageNum);
      const token = localStorage.getItem('token');
      const response = await api.get(
  `/walmart/products?limit=50&page=${pageNum}&search=${encodeURIComponent(searchQuery)}`,
  {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log('Walmart products response:', response.data);
      const walmartProducts = response.data.products.filter(product => {
        const includesWalmart = product.platform && product.platform.includes('Walmart');
        console.log(`Product SKU: ${product.sku}, Platform: ${product.platform}, Includes Walmart: ${includesWalmart}`);
        return includesWalmart;
      });
      console.log('Filtered Walmart products:', walmartProducts);
      if (walmartProducts.length === 0 && pageNum === 1) {
        setError('No Walmart products found. Try importing inventory.');
      }
      setProducts(walmartProducts);
      setTotalPages(response.data.totalPages || 1);
      localStorage.setItem('walmartProducts', JSON.stringify(walmartProducts));
    } catch (err) {
      console.error('Error fetching Walmart products:', err.response?.data || err);
      if (err.response?.status === 401) {
        console.warn('401 Unauthorized: Redirecting to login');
        localStorage.removeItem('token');
        navigate('/login');
        return;
      }
      const errorMsg = err.response?.data?.error || err.response?.data?.message || err.message;
      if (err.response?.status === 429) {
        setError('Rate limit exceeded. Please try again later.');
      } else {
        setError(`Failed to load Walmart inventory: ${errorMsg}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleImportWalmart = async () => {
    if (!checkAuthAndRedirect()) return;
    try {
      setLoading(true);
      console.log('Importing Walmart inventory...');
      const token = localStorage.getItem('token');
      const response = await api.get('/walmart/import', {
        headers: { Authorization: `Bearer ${token}` },
      });
      console.log('Import response:', response.data);
      alert(response.data.message);
      await fetchProducts(page);
    } catch (err) {
      console.error('Import error:', err);
      if (err.response?.status === 401) {
        console.warn('401 Unauthorized: Redirecting to login');
        localStorage.removeItem('token');
        navigate('/login');
      } else {
        const errorMsg = err.response?.data?.error || err.response?.data?.details || err.message;
        if (err.response?.status === 429) {
          setError('Rate limit exceeded during import. Please try again later.');
        } else {
          setError(`Failed to import Walmart inventory: ${errorMsg}`);
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditProduct = (sku) => {
    navigate(`/products/edit/${sku}`);
  };

  const handleDeleteProduct = async (sku) => {
    if (!window.confirm(`Are you sure you want to delete product ${sku}?`)) return;
    try {
      const token = localStorage.getItem('token');
      await api.delete(`/products/${sku}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProducts(products.filter(product => product.sku !== sku));
      localStorage.setItem('walmartProducts', JSON.stringify(products.filter(product => product.sku !== sku)));
      alert(`Product ${sku} deleted successfully`);
    } catch (err) {
      console.error('Error deleting product:', err);
      if (err.response?.status === 401) {
        console.warn('401 Unauthorized: Redirecting to login');
        localStorage.removeItem('token');
        navigate('/login');
      } else {
        setError(`Failed to delete product ${sku}: ${err.response?.data?.error || err.message}`);
      }
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      await checkConnection();
      setLoading(false);
    };
    fetchData();

    const query = new URLSearchParams(location.search);
    if (query.get('disconnected') === 'true') {
      console.log('Detected disconnect, rechecking connection...');
      setIsConnected(false);
      setError('Walmart has been disconnected. Please import again.');
    }

    const errorMsg = query.get('error');
    if (errorMsg) {
      setError(decodeURIComponent(errorMsg));
    }
  }, [location]);

  if (loading) return <Container className="mt-5"><h1>Loading...</h1></Container>;

  return (
    <Container className="mt-5">
      <h1>Walmart Inventory</h1>
      {error && (
        <Alert variant="danger" onClose={() => setError(null)} dismissible>
          {error}
        </Alert>
      )}
      {connectionLoading ? (
        <div>
          <Spinner animation="border" role="status" className="mr-2" />
          <span>Checking Walmart connection...</span>
        </div>
      ) : !isConnected ? (
        <div>
          <p>Walmart is not connected. Please contact support to configure Walmart credentials.</p>
        </div>
      ) : (
        <>
          {/* ✅ Search Bar + Action Buttons */}
<div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
  <div className="d-flex gap-2 align-items-center w-100 flex-wrap">
    <input
      type="text"
      className="form-control w-50"
      placeholder="Search by SKU, Name, or Platform..."
      value={searchQuery}
      onChange={(e) => setSearchQuery(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') fetchProducts(1);
      }}
    />
    <Button
      variant="secondary"
      onClick={() => {
        setSearchQuery('');
        fetchProducts(1);
      }}
    >
      Clear
    </Button>
    <Button
      variant="success"
      onClick={() => fetchProducts(1)}
      disabled={loading}
    >
      {loading ? 'Searching...' : 'Search'}
    </Button>
  </div>

  {/* 🟢 Optional Action Buttons (keep your existing logic here) */}
  <div className="d-flex gap-2">
    <Button variant="success" onClick={handleImportWalmart} disabled={loading}>
      {loading ? 'Importing...' : 'Import from Walmart'}
    </Button>
    <Button
      variant="outline-danger"
      onClick={async () => {
        try {
          await api.get('/walmart/disconnect', {
            headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
          });
          setIsConnected(false);
          setError('Walmart disconnected. Please import to reconnect.');
        } catch (err) {
          setError('Failed to disconnect Walmart: ' + (err.response?.data?.error || err.message));
        }
      }}
    >
      Disconnect Walmart
    </Button>
    <RefreshConnectionButton platform="Walmart" onRefresh={() => fetchProducts(page)} />
  </div>
</div>

          <Table striped bordered hover>
            <thead>
              <tr>
                <th>SKU</th>
                <th>Name</th>
                <th>Price ($)</th>
                <th>Stock</th>
                <th>Platform</th>
                <th>First Walmart Image</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {products.length === 0 ? (
                <tr>
                  <td colSpan="7" className="text-center">
                    No Walmart products found. Try importing inventory.
                  </td>
                </tr>
              ) : (
                products.map(product => (
                  <tr key={product.sku}>
                    <td>{product.sku}</td>
                    <td>{product.name}</td>
                    <td>{product.price || 'N/A'}</td>
                    <td>{product.stock || 0}</td>
                    <td>{product.platform ? product.platform.split(',').join(', ') : 'None'}</td>
                    <td>
                      <AuthenticatedImage
                        src={
  product.image || 
  (Array.isArray(product.walmart_images) && product.walmart_images.length > 0 ? product.walmart_images[0] : null)
}

                        alt={`First ${product.platform || 'Walmart'} Image for ${product.sku}`}
                        sku={product.sku}
                        platform={product.platform || 'Walmart'}
                      />
                    </td>
                    <td>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => handleEditProduct(product.sku)}
                      >
                        Edit
                      </Button>{' '}
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => handleDeleteProduct(product.sku)}
                      >
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </Table>
          <Pagination className="justify-content-center">
            <Pagination.Prev
              onClick={() => {
                if (page > 1) {
                  setPage(page - 1);
                  fetchProducts(page - 1);
                }
              }}
              disabled={page === 1}
            />
            {Array.from({ length: totalPages }, (_, i) => (
              <Pagination.Item
                key={i + 1}
                active={i + 1 === page}
                onClick={() => {
                  setPage(i + 1);
                  fetchProducts(i + 1);
                }}
              >
                {i + 1}
              </Pagination.Item>
            ))}
            <Pagination.Next
              onClick={() => {
                if (page < totalPages) {
                  setPage(page + 1);
                  fetchProducts(page + 1);
                }
              }}
              disabled={page === totalPages}
            />
          </Pagination>
        </>
      )}
    </Container>
  );
}

export default WalmartInventory;